// Tip sources and tips paid in (legacy `tip_sources`, `paid_in_tips`; field notes
// backoffice.settings.order-checkout.tips.staff-tips and register.dialog.tips-paid-in). A tip source is location config
// (ADR-015, `labor.tip_source`), synced to the registers that offer it; a tip paid in is a device fact on its own stream
// (`tip_paid_in:<id>`), decided by core's `labor/tip-paid-in` and reported at end of day. No money moves.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { EpochMs, LocalDate, LocationId, Money, TipPaidInId, TipSourceId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten } from '../config/index.ts';
import { LocationTime } from './index.ts';

const optionalText = z.string().trim().max(255).nullable();

export const TipSourceFields = z
  .object({ name: z.string().trim().min(1).max(255), description: optionalText })
  .meta({ id: 'TipSourceFields' });

export const TipSourceView = TipSourceFields.extend({ tipSourceId: TipSourceId, locationId: LocationId, version: z.int().nonnegative() }).meta({ id: 'TipSourceView' });

export const TipSourceNotFound = defineError({
  code: 'labor.tip_source_not_found', httpStatus: 404, message: 'errors.labor.tip_source_not_found', retryable: false,
  details: z.object({ tipSourceId: TipSourceId }),
});

const sourceAdmin = {
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: 'settings.staff.manage', output: TipSourceView,
  risk: 'low', dryRun: true, agent: { expose: false },
} as const;

export const UpsertTipSource = defineCommand({
  ...sourceAdmin,
  name: 'labor.upsertTipSource',
  summary: 'Create a tip source at a location (Create Tip Source), or change its name or description.',
  input: z.object({ tipSourceId: TipSourceId, locationId: LocationId, changes: TipSourceFields.partial() }),
  errors: [TipSourceNotFound, CloudAuthoritative],
  legacy: ['POST /v1/tip-sources', 'PUT /v1/tip-sources/{id}'],
});

export const DeleteTipSource = defineCommand({
  ...sourceAdmin,
  name: 'labor.deleteTipSource',
  summary: 'Delete a tip source; tips already paid in keep its name.',
  input: z.object({ tipSourceId: TipSourceId, locationId: LocationId }),
  errors: [TipSourceNotFound, CloudAuthoritative],
  legacy: ['DELETE /v1/tip-sources/{id}'],
});

export const ListTipSources = defineQuery({
  name: 'labor.listTipSources',
  summary: "A location's tip sources, by name.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ tipSources: z.array(TipSourceView) }),
  // The Back Office reads it (Employee Breaks, Staff Tips, the timecard editor); registers read the synced config.
  permission: { session: 'user' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/tip-sources'],
});

// ---- tips paid in ----------------------------------------------------------------------------------------------------
const ActorRef = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('staff'), employeeId: z.uuid() }),
    z.object({ kind: z.literal('user'), userId: z.uuid() }),
    z.object({ kind: z.literal('device'), deviceId: z.uuid() }),
    z.object({ kind: z.literal('system'), job: z.string().max(64) }),
  ])
  .meta({ id: 'TipPaidInActor' });

export const TipPaidIn = defineEvent({
  aggregate: 'tip_paid_in',
  deviceConsumed: true,
  name: 'TipPaidIn',
  version: 1,
  summary: 'Tips processed outside Radish, reported against a tip source for the business day (no money moves).',
  payload: z.object({
    tipPaidInId: TipPaidInId, tipSourceId: TipSourceId, sourceName: z.string().max(255), amount: Money, businessDate: LocalDate, recordedBy: ActorRef, recordedAt: EpochMs,
  }),
  verifiable: ['tipPaidInId', 'tipSourceId', 'amount', 'businessDate'],
  merge: 'lifecycle',
});

export const tipPaidInEvents = [TipPaidIn] as const;

export const TipPaidInRefused = defineError({
  code: 'labor.tip_paid_in_refused', httpStatus: 422, message: 'errors.labor.tip_paid_in_refused', retryable: false,
  details: z.object({ rule: z.enum(['amount_not_positive', 'unknown_tip_source']) }),
});

export const TipPaidInView = z
  .object({
    tipPaidInId: TipPaidInId, locationId: LocationId, tipSourceId: TipSourceId, sourceName: z.string(), amount: Money, businessDate: LocalDate,
    recordedAt: LocationTime, recordedByEmployeeId: z.uuid().nullable(),
  })
  .meta({ id: 'TipPaidInView' });

export const RecordTipPaidIn = defineCommand({
  name: 'labor.recordTipPaidIn',
  summary: 'Report tips already processed outside Radish against one of the location tip sources (Manager Activities › Tips Paid In).',
  stream: { aggregate: 'tip_paid_in', id: (input: { tipPaidInId: string }) => input.tipPaidInId },
  input: z.object({ tipPaidInId: TipPaidInId, locationId: LocationId, tipSourceId: TipSourceId, amount: Money }),
  output: TipPaidInView,
  permission: { session: 'staff' },
  availability: 'location',
  origin: 'device',
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [TipPaidIn],
  errors: [TipPaidInRefused],
  legacy: ['POST /v1/tip-paid-in'],
});

export const ListTipsPaidIn = defineQuery({
  name: 'labor.listTipsPaidIn',
  // E-09: a back-office session also needs Payroll Reports (legacy checked it in the browser only).
  backofficeAlso: ['reports.payroll.view'],
  summary: "A location's tips paid in by business date (inclusive range), for end-of-day reporting.",
  input: z.object({ locationId: LocationId, from: LocalDate, to: LocalDate }),
  output: z.object({ tipsPaidIn: z.array(TipPaidInView) }),
  permission: 'reports.view',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
});

/**
 * The payroll report's Tips per employee per business day (legacy `GET /v1/reports/payroll` `tips`): with "Payroll Tips"
 * on (`labor.tips.payroll_from_tenders`), the card tips on the employee's closed orders; off, the tip payouts recorded
 * to them (a payout counts on the pool day it paid out).
 */
export const PayrollTips = defineQuery({
  name: 'labor.payrollTips',
  // E-09: a back-office session also needs Payroll Reports (legacy checked it in the browser only).
  backofficeAlso: ['reports.payroll.view'],
  summary: "Tips per employee per business day for the payroll report: card tips on their orders, or tip payouts made to them.",
  input: z.object({ locationId: LocationId, from: LocalDate, to: LocalDate }),
  output: z.object({
    source: z.enum(['tenders', 'payouts']),
    tips: z.array(z.object({ employeeId: z.uuid(), businessDate: LocalDate, amount: Money })),
  }),
  permission: 'reports.view',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/reports/payroll (tips)'],
});

// ---- payroll summary -------------------------------------------------------------------------------------------------
const PayrollFigures = z
  .object({
    /** Seconds: hours are hundredths of an hour on the wire (36 s each), as payroll counts them. */
    regularSeconds: z.int().nonnegative(),
    overtimeSeconds: z.int().nonnegative(),
    doubleTimeSeconds: z.int().nonnegative(),
    regularPay: Money,
    overtimePay: Money,
    doubleTimePay: Money,
    /**
     * Regular + overtime + double time (+ the meal premium) as the report prints it: legacy adds the unrounded pay in
     * doubles and rounds once, so it can differ by a cent from the three cells added; on the location's totals it is
     * truncated to the cent, as legacy's Total row is.
     */
    totalPay: Money,
    tips: Money,
  })
  .meta({ id: 'PayrollFigures' });

/**
 * A day's pay as legacy keeps it, in hundredths of a cent (canonical integer strings): legacy rounds each day to 0.01 ¢
 * and only the printed figures to the cent, so the client recomputes a filtered Total row from these (core
 * `legacyTotalsRow`).
 */
const PayrollExactPay = z
  .object({ regular: z.string().regex(/^(0|-?[1-9]\d*)$/), overtime: z.string().regex(/^(0|-?[1-9]\d*)$/), doubleTime: z.string().regex(/^(0|-?[1-9]\d*)$/) })
  .meta({ id: 'PayrollExactPay' });

/**
 * One owed business day of an employee's California meal premium on the payroll report (legacy `pay.premium_days`): one
 * hour of base wage. `outsideRange` marks a day read from the 3 days either side of the range: it is paid in the period
 * it falls in, and the report only hints at it.
 */
export const PayrollMealPremiumDay = z
  .object({
    businessDate: LocalDate,
    state: z.enum(['owed', 'owed_late']),
    premium: Money,
    outsideRange: z.boolean(),
  })
  .meta({ id: 'PayrollMealPremiumDay' });

/**
 * Payroll Summary (legacy `GET /v1/reports/payroll`, field note backoffice.reports.management.payroll): per employee per
 * business day the regular, overtime and double-time hours and pay (core's payroll, stored in the labor rollups), with
 * the Tips column as `labor.payrollTips` reads it, plus totals, and the California meal premium (core
 * `assessMealPremiums`) when the location flags it. Every employee assigned to the location is listed, with
 * zeros when they did not work; the page filters by status and employee.
 */
export const PayrollSummary = defineQuery({
  name: 'labor.payrollSummary',
  // E-09: a back-office session also needs Payroll Reports (legacy checked it in the browser only).
  backofficeAlso: ['reports.payroll.view'],
  summary: 'Hours, pay and tips per employee per business day for a range (Payroll Summary and its employee detail).',
  input: z.object({ locationId: LocationId, from: LocalDate, to: LocalDate }),
  output: z.object({
    tipsSource: z.enum(['tenders', 'payouts']),
    employees: z.array(z.object({
      employeeId: z.uuid(),
      days: z.array(PayrollFigures.extend({ businessDate: LocalDate, exactPay: PayrollExactPay })),
      totals: PayrollFigures,
      /**
       * With "Flag potential meal-period premiums" on (`labor.meal_premium.flagging_enabled`): the premium owed in the
       * range (null when one is owed but the wage is under $7.25, never $0.00) and the owed days. Null when the flag is off.
       */
      mealPremium: z.object({ amount: Money.nullable(), days: z.array(PayrollMealPremiumDay) }).nullable(),
    })),
    totals: PayrollFigures,
    /** The location flags meal-period premiums: the report shows the Meal Premium column. */
    mealPremiumEnabled: z.boolean(),
  }),
  permission: 'reports.view',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/reports/payroll'],
});

// ---- the payroll detail's timecard report ------------------------------------------------------------------------------
/** Hours over pay as the report prints them ("8.48", "$63.22"; PHP `number_format`). */
const TimecardReportCell = z.object({ hours: z.string(), pay: z.string() }).meta({ id: 'TimecardReportCell' });
const TimecardReportFigures = z
  .object({ total: TimecardReportCell, regular: TimecardReportCell, overtime: TimecardReportCell, doubleTime: TimecardReportCell })
  .meta({ id: 'TimecardReportFigures' });
/** A break in a report cell: its definition's name ("N/A" when unnamed) and its two ends ("01:05 PM"; "" while open). */
const TimecardReportBreak = z.object({ name: z.string(), out: z.string(), in: z.string() }).meta({ id: 'TimecardReportBreak' });

/**
 * The payroll detail's "Download Timecard Report" (legacy `GET /v1/reports/timecard/pdf`, the Blade view
 * reports/timecard-report): one employee's Report Summary, then each business day of the range with hours, its figures
 * and its cards, every figure printed as legacy's file prints it (core `legacyTimecardReport`). The back office lays
 * the PDF out.
 */
export const PayrollTimecardReport = defineQuery({
  name: 'labor.payrollTimecardReport',
  backofficeAlso: ['reports.payroll.view'],
  summary: "One employee's timecard report for a range: the summary, then each worked day with its cards and breaks.",
  input: z.object({ locationId: LocationId, employeeId: z.uuid(), from: LocalDate, to: LocalDate }),
  output: z.object({
    summary: TimecardReportFigures,
    /** Only days with hours; none: "No time cards found for this period". */
    days: z.array(TimecardReportFigures.extend({
      businessDate: LocalDate,
      /** "Wednesday, August 05, 2026". */
      heading: z.string(),
      cards: z.array(z.object({
        clockIn: z.string(),
        clockOut: z.string(),
        /** The clock-out's date when it falls on a later calendar day than the clock-in. */
        clockOutDate: LocalDate.nullable(),
        paidBreaks: z.array(TimecardReportBreak),
        unpaidBreaks: z.array(TimecardReportBreak),
        hours: z.string(),
      })),
    })),
  }),
  permission: 'reports.view',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/reports/timecard/pdf'],
});

export const tipCommands = [UpsertTipSource, DeleteTipSource, RecordTipPaidIn] as const;
export const tipQueries = [ListTipSources, ListTipsPaidIn, PayrollTips, PayrollSummary, PayrollTimecardReport] as const;
